import {
	SpotifyCreateData,
	SpotifyCreateLevels,
	SpotifyEntityCreateStatus,
	SpotifySuperGridMutationsConfig,
} from 'wb-spotify/app/routes/superGrid/mutator/utils';
import {
	GraphQLErrorWithFormValidation,
	mapValidationErrors,
	SpotifyLevel,
} from 'wb-spotify/app/routes/superGrid/utils';
import {
	flatten, isEmpty, omitBy, uniqBy,
} from 'lodash';
import {
	useSupergridMutations,
	useSuperGridValidate,
} from '@aleph/supergrid';
import {
	alephToast,
	NotificationToast,
	NotificationType,
	useUrlParams,
	translate,
	useCreateLoading,
} from '@aleph/shared-components';
import useCreateSpotifyCampaigns from 'wb-spotify/app/routes/superGrid/mutator/hooks/useCreateCampaigns';
import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import useCreateSpotifyGroups from 'wb-spotify/app/routes/superGrid/mutator/hooks/useCreateGroups';
import useCreateSpotifyAds from 'wb-spotify/app/routes/superGrid/mutator/hooks/useCreateAds';
import { LEVEL_ID_FILTER } from 'wb-spotify/app/routes/superGrid/actionRow/filters/utils';
import { preValidateSpotifyEntities } from 'wb-spotify/app/routes/superGrid/validation/utils';
import { captureException } from '@sentry/react';
import { AdStatus, CampaignStatus, GroupStatus } from 'wb-spotify/graphql/generated';
import { useNavigate, useParams } from 'react-router-dom';

// If entity is in draft on the BE its not published.
// We set the initial status to be active so once user decides to publish it will be in active state.
const STATUS_MAP = {
	campaign: {
		[SpotifyEntityCreateStatus.ACTIVE]: CampaignStatus.Active,
		[SpotifyEntityCreateStatus.DRAFT]:  CampaignStatus.Draft,
	},
	group: {
		[SpotifyEntityCreateStatus.ACTIVE]: GroupStatus.Active,
		[SpotifyEntityCreateStatus.DRAFT]:  GroupStatus.Draft,
	},
	ad: {
		[SpotifyEntityCreateStatus.ACTIVE]: AdStatus.Active,
		[SpotifyEntityCreateStatus.DRAFT]:  AdStatus.Draft,
	},
};

const useConfirmSpotifyMutations = () => {
	const navigate = useNavigate();
	const { params, setUrlParams, setUrlParam } = useUrlParams({
		navigate,
		useParams,
	});
	const [allCreatedEntites, setAllCreatedEntites] = useState<Record<SpotifyCreateLevels, Record<string, any>[]>>({
		[SpotifyCreateLevels.CAMPAIGN]: [],
		[SpotifyCreateLevels.GROUP]:    [],
		[SpotifyCreateLevels.AD]:       [],
	});

	const [generalErrors, setGeneralErrors] = useState<{
		entityName: string
		message: string
	}[]>([]);

	const client = useApolloClient();

	const [createCampaigns, { loading: campaignsLoading }] = useCreateSpotifyCampaigns();
	const [createGroups, { loading: groupsLoading }] = useCreateSpotifyGroups();
	const [createAds, { loading: adsLoading }] = useCreateSpotifyAds();
	const { context: { validations, setValidations } } = useSuperGridValidate();
	const { setCreateLoading } = useCreateLoading();

	const {
		mutations, unsetMutations, changeMutation,
	} = useSupergridMutations<SpotifySuperGridMutationsConfig>();

	useEffect(() => {
		if (!flatten(Object.values(allCreatedEntites))?.length) {
			return;
		}

		setTimeout(() => {
			setUrlParams({
				...params,
				level: allCreatedEntites[SpotifyCreateLevels.CAMPAIGN].length > 0
					? SpotifyCreateLevels.CAMPAIGN
					: allCreatedEntites[SpotifyCreateLevels.GROUP].length > 0
						? SpotifyCreateLevels.GROUP
						: SpotifyCreateLevels.AD,
				autoExpand: flatten(Object.values(allCreatedEntites)).filter(
					entity => entity.groupId || entity.campaignId, // only expand groups and campaign,
				).map(entity => entity?.id),
				filters: omitBy({
					[LEVEL_ID_FILTER[SpotifyCreateLevels.CAMPAIGN]]: [{ value: allCreatedEntites[SpotifyCreateLevels.CAMPAIGN].map(entity => entity?.campaignId).filter(id => !!id) }],
					[LEVEL_ID_FILTER[SpotifyCreateLevels.GROUP]]:    [{ value: allCreatedEntites[SpotifyCreateLevels.GROUP].map(entity => entity?.groupId).filter(id => !!id) }],
					[LEVEL_ID_FILTER[SpotifyCreateLevels.AD]]:       [{ value: allCreatedEntites[SpotifyCreateLevels.AD].map(entity => entity?.adId).filter(id => !!id) }],
				}, val => isEmpty(val?.[0]?.value)),
			});
		});
	}, [allCreatedEntites]);

	// Helper to check if an error has the asset not synced error
	const hasAssetNotSyncedError = (error: GraphQLErrorWithFormValidation) => {
		if (!error?.form) {
			return false;
		}

		return Object.values(error.form).some(
			fieldError => fieldError?.message === 'sp:adAssetNotSynced',
		);
	};

	// Helper to filter out asset not synced errors from validation errors
	const filterOutAssetNotSyncedErrors = (errors: GraphQLErrorWithFormValidation[]) => errors.filter(error => !hasAssetNotSyncedError(error));

	const getGeneralErrors = (errors?: readonly GraphQLErrorWithFormValidation[]) => {
		const mutationEntities = Object.values(mutations).flat();

		const mappedErrors = errors
			?.filter(error => {
				// Include errors without form property (general errors)
				if (!error?.form) {
					return true;
				}

				// Treat "sp:adAssetNotSynced" as a general error instead of field error
				return hasAssetNotSyncedError(error);
			})
			?.map(
				error => {
					// For asset not synced errors, extract the message from the form
					let errorMessage = error?.debugMessage ?? error?.message ?? 'Unknown Error';
					let entityName = mutationEntities?.find(mutation => mutation.hash === error?.hash)?.name ?? 'General error';

					if (error?.form) {
						const assetNotSyncedField = Object.entries(error.form).find(
							([, fieldError]) => fieldError?.message === 'sp:adAssetNotSynced',
						);

						if (assetNotSyncedField) {
							errorMessage = assetNotSyncedField[1]?.message ?? errorMessage;
							entityName = 'Ads';
						}
					}

					return ({
						entityName,
						message: translate(errorMessage),
					});
				},
			) ?? [];

		return mappedErrors;
	};

	const mutateEntities = async (status: SpotifyEntityCreateStatus) => {
		try {
			// CAMPAIGNS STEP
			const queuedCampaigns = mutations.campaign?.filter(
				campaign => campaign.hash && !campaign.isExistingCreate,
			) ?? [] as SpotifyCreateData[];

			const preValidations = await preValidateSpotifyEntities(mutations);

			if (preValidations.length > 0) {
				setValidations(preValidations);
				setCreateLoading(false);

				return false;
			}

			const { data: campaignsData, errors: campaignErrors } = await createCampaigns(
				queuedCampaigns,
				STATUS_MAP.campaign[status],
			);
			const createdCampaigns = campaignsData?.createCampaigns ?? [];
			const campaignValidationErrors = mapValidationErrors(
				(campaignErrors as unknown as GraphQLErrorWithFormValidation[]) ?? [],
				SpotifyLevel.CAMPAIGN,
			);

			// GROUPS STEP
			const queuedGroups = (mutations.group?.filter(group => group.hash && !group.isExistingCreate)
				.map(group => {
					const availableCampaigns = (
						createdCampaigns.length
							? createdCampaigns
							: allCreatedEntites[SpotifyCreateLevels.CAMPAIGN]
					);

					return ({
						...group,
						parent: {
							...group.parent,
							id: (
								availableCampaigns
									.find(
										campaign => campaign.campaignHash === group.parent?.hash,
									)?.campaignId ?? group.parent?.id
							),
						},
					});
				})
				.filter(group => !!group?.parent?.id) ?? []);

			const { data: groupsData, errors: groupErrors } = await createGroups(
				queuedGroups,
				STATUS_MAP.group[status],
			);

			const createdGroups = groupsData?.createGroups ?? [];
			const groupValidationErrors = mapValidationErrors(
				(groupErrors as unknown as GraphQLErrorWithFormValidation[]) ?? [],
				SpotifyLevel.GROUP,
			);

			// ADS STEP
			const queuedAds = (
				mutations.ad?.filter(ad => ad.hash && !ad.isExistingCreate).map(ad => {
					// If groups are created, use them to find the parent,
					// otherwise use the allCreatedEntites since the groups might already be created
					const availableGroups = (
						createdGroups.length
							? createdGroups
							: allCreatedEntites[SpotifyCreateLevels.GROUP]
					);

					return ({
						...ad,
						adStatus: status,
						parent:   {
							...ad.parent,
							id: (
								availableGroups
									.find(
										group => group.groupHash === ad.parent?.hash,
									)?.groupId ?? ad.parent?.id
							),
						},
					});
				}).filter(ad => !!ad?.parent?.id) ?? []) as SpotifyCreateData[];

			const { data: adsData, errors: adMutationErrors } = await createAds(queuedAds);
			const adErrors = adMutationErrors as unknown as GraphQLErrorWithFormValidation[];

			const createdAds = adsData?.createAds ?? [];
			const adValidationErrors = [
				...mapValidationErrors(
					filterOutAssetNotSyncedErrors(adErrors ?? []),
					SpotifyLevel.AD,
				),
			];

			const validationErrors = [...campaignValidationErrors, ...groupValidationErrors, ...adValidationErrors].map(
				error => ({
					...error,
					message: translate(error.message),
				}),
			);

			setGeneralErrors([
				...getGeneralErrors(campaignErrors),
				...getGeneralErrors(groupErrors),
				...getGeneralErrors(adErrors),
			]);

			setValidations(uniqBy([
				...validations,
				...validationErrors,
			], validation => `${validation.id}_${validation.field}_${validation.level}`));

			// TODO translate
			if (flatten<any>([createdCampaigns, createdGroups, createdAds]).length > 0) {
				alephToast(
					<NotificationToast type={NotificationType.Success}
						title={<div className="text-[12px] text-white">
							{
								createdCampaigns?.length > 0
							&&	<div>
								<span className="font-bold">
									{createdCampaigns?.length} out of {mutations.campaign?.length ?? 0} campaigns
								</span> scheduled for creation
							</div>
							}
							{
								createdGroups?.length > 0
							&&	<div>
								<span className="font-bold">
									{createdGroups?.length} out of {mutations.group?.length ?? 0} groups
								</span> scheduled for creation
							</div>
							}
							{
								createdAds?.length > 0
							&&	<div>
								<span className="font-bold">
									{createdAds?.length} out of {mutations.ad?.length ?? 0} ads
								</span> scheduled for creation
							</div>
							}
						</div>}
					/>,
				);
			}

			setAllCreatedEntites(curr => ({
				[SpotifyCreateLevels.CAMPAIGN]: [...curr[SpotifyCreateLevels.CAMPAIGN], ...createdCampaigns],
				[SpotifyCreateLevels.GROUP]:    [...curr[SpotifyCreateLevels.GROUP], ...createdGroups],
				[SpotifyCreateLevels.AD]:       [...curr[SpotifyCreateLevels.AD], ...createdAds],
			}));

			setCreateLoading(false);

			// If entity was created then update the mutation isExistingCreate to true
			// so in UI it appears as not editable
			Object.values(SpotifyCreateLevels).forEach(level => {
				mutations[level]?.forEach(mutation => {
					if (
						level === SpotifyCreateLevels.CAMPAIGN
							&& createdCampaigns.find(campaign => campaign.campaignHash === mutation.hash)
					) {
						changeMutation({
							...mutation,
							isExistingCreate: true,
						});
					}

					if (
						level === SpotifyCreateLevels.GROUP
							&& createdGroups.find(group => group.groupHash === mutation.hash)
					) {
						changeMutation({
							...mutation,
							isExistingCreate: true,
						});
					}

					if (level === SpotifyCreateLevels.AD && createdAds.find(ad => ad.adHash === mutation.hash)) {
						changeMutation({
							...mutation,
							isExistingCreate: true,
						});
					}
				});
			});

			return [...validationErrors, ...campaignErrors ?? [], ...groupErrors ?? [], ...adErrors ?? []].length === 0;
		} catch (e: any) {
			setGeneralErrors([
				...generalErrors,
				{
					entityName: 'General values Error',
					message:    e?.message ?? 'Something went wrong',
				},
			]);

			captureException(e);
			setCreateLoading(false);

			return false;
		}
	};

	const confirmMutations = async (status: SpotifyEntityCreateStatus) => {
		setCreateLoading(true);
		const sucessfullyMutated = await mutateEntities(status);

		if (sucessfullyMutated) {
			await client.clearStore();
			setUrlParam('creationLevel', undefined);
			setUrlParam('inCreate', false);
			unsetMutations([
				...(allCreatedEntites[SpotifyCreateLevels.CAMPAIGN] ?? []).map(campaign => campaign.campaignHash ?? ''),
				...(allCreatedEntites[SpotifyCreateLevels.GROUP] ?? []).map(group => group.groupHash ?? ''),
				...(allCreatedEntites[SpotifyCreateLevels.AD] ?? []).map(ad => ad.adHash ?? ''),
			]);
		}
	};

	useEffect(() => {
		if ((generalErrors ?? []).length === 0) {
			return;
		}

		alephToast(
			({ closeToast }) => (<NotificationToast
				type={NotificationType.Error}
				title={generalErrors.map(msg => (
					<div className="flex gap-[8px]">
						<div className="font-bold">{msg.entityName}:</div>
						<div>{msg.message}</div>
					</div>
				))}
				closeToast={closeToast}
			/>),
			{
				autoClose: false,
			},
		);
	}, [generalErrors]);

	return {
		confirmMutations,
		loading: campaignsLoading || groupsLoading || adsLoading,
	};
};

export default useConfirmSpotifyMutations;
